/**
 * Drawer, Modal and Sheet: the three floating surfaces, sharing one open/close
 * lifecycle so their motion cannot diverge.
 *
 * `useExitAnimation` is the piece that matters. Unmounting on the click would
 * make every panel snap away; instead the component keeps rendering through the
 * 220ms exit and only then drops out. Under `prefers-reduced-motion` the wait
 * is zero, so a caller is never left holding a panel that is visually already
 * gone.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { X } from 'lucide-react';
import { EXIT_MS, exitDelay } from '../lib/motion.ts';

function useExitAnimation(open: boolean): { mounted: boolean; leaving: boolean } {
  const [mounted, setMounted] = useState(open);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setLeaving(false);
      return;
    }
    if (!mounted) return;
    setLeaving(true);
    const timer = window.setTimeout(() => {
      setMounted(false);
      setLeaving(false);
    }, exitDelay(EXIT_MS));
    return () => window.clearTimeout(timer);
  }, [open, mounted]);

  return { mounted, leaving };
}

/** Escape closes, and while a surface is open the page behind it does not scroll. */
function useDismiss(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
}

/** Focus lands inside the surface on open and returns where it came from. */
function useFocusTrap(open: boolean, ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const first = node?.querySelector<HTMLElement>(
      'input:not([type=hidden]), select, textarea, button, [href], [tabindex]:not([tabindex="-1"])',
    );
    (first ?? node)?.focus({ preventScroll: true });
    return () => previous?.focus?.({ preventScroll: true });
  }, [open, ref]);
}

interface SurfaceProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Slide-over width. Defaults to a comfortable single-column form. */
  width?: number;
}

export function Drawer({ open, onClose, title, subtitle, children, footer, width = 460 }: SurfaceProps) {
  const { mounted, leaving } = useExitAnimation(open);
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(open, onClose);
  useFocusTrap(open, ref);
  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-[8000] flex justify-end">
      <button
        type="button"
        aria-label="Close panel"
        onClick={onClose}
        className={`absolute inset-0 cursor-default ${leaving ? 'anim-scrim-out' : 'anim-scrim-in'}`}
        style={{ background: 'rgb(20 16 12 / 0.35)' }}
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={`relative flex h-full w-full flex-col ${leaving ? 'anim-drawer-out' : 'anim-drawer-in'}`}
        style={{
          maxWidth: width,
          background: 'var(--sp-surface)',
          borderLeft: '1px solid var(--sp-border)',
          boxShadow: 'var(--sp-shadow-4)',
        }}
      >
        <header className="flex items-start gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[17px] font-bold text-ink">{title}</h2>
            {subtitle ? <p className="mt-0.5 truncate text-[13px] text-muted">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            className="btn btn-icon btn-ghost"
            onClick={onClose}
            title="Close|Escape closes this panel too."
            aria-label="Close"
          >
            <X size={17} aria-hidden="true" />
          </button>
        </header>
        <div className="thin-scroll flex-1 overflow-y-auto px-5 py-5">{children}</div>
        {footer ? (
          <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">{footer}</footer>
        ) : null}
      </div>
    </div>
  );
}

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = 520,
}: SurfaceProps) {
  const { mounted, leaving } = useExitAnimation(open);
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(open, onClose);
  useFocusTrap(open, ref);
  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-[8500] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className={`absolute inset-0 cursor-default ${leaving ? 'anim-scrim-out' : 'anim-scrim-in'}`}
        style={{ background: 'rgb(20 16 12 / 0.4)' }}
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={`relative flex max-h-full w-full flex-col overflow-hidden ${
          leaving ? 'anim-modal-out' : 'anim-modal-in'
        }`}
        style={{
          maxWidth: width,
          background: 'var(--sp-surface)',
          border: '1px solid var(--sp-border)',
          borderRadius: 'var(--sp-radius-lg)',
          boxShadow: 'var(--sp-shadow-4)',
        }}
      >
        <header className="flex items-start gap-3 px-5 pb-3 pt-5">
          <div className="min-w-0 flex-1">
            <h2 className="text-[17px] font-bold text-ink">{title}</h2>
            {subtitle ? <p className="mt-1 text-[13.5px] leading-snug text-muted">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            className="btn btn-icon btn-ghost -mt-1"
            onClick={onClose}
            title="Close"
            aria-label="Close"
          >
            <X size={17} aria-hidden="true" />
          </button>
        </header>
        <div className="thin-scroll flex-1 overflow-y-auto px-5 pb-1">{children}</div>
        <footer className="flex items-center justify-end gap-2 px-5 py-4">{footer}</footer>
      </div>
    </div>
  );
}

/** Phone-first bottom sheet, used by the staff shell where a side drawer would
 *  be reaching to the far corner of a one-handed grip. */
export function Sheet({ open, onClose, title, subtitle, children, footer }: Omit<SurfaceProps, 'width'>) {
  const { mounted, leaving } = useExitAnimation(open);
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(open, onClose);
  useFocusTrap(open, ref);
  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-[8500] flex items-end justify-center">
      <button
        type="button"
        aria-label="Close sheet"
        onClick={onClose}
        className={`absolute inset-0 cursor-default ${leaving ? 'anim-scrim-out' : 'anim-scrim-in'}`}
        style={{ background: 'rgb(20 16 12 / 0.4)' }}
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={`relative flex max-h-[88vh] w-full flex-col ${leaving ? 'anim-sheet-out' : 'anim-sheet-in'}`}
        style={{
          background: 'var(--sp-surface)',
          borderTopLeftRadius: 'var(--sp-radius-xl)',
          borderTopRightRadius: 'var(--sp-radius-xl)',
          boxShadow: 'var(--sp-shadow-4)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div className="flex justify-center pt-2.5">
          <span
            aria-hidden="true"
            style={{ width: 38, height: 4, borderRadius: 999, background: 'var(--sp-border-strong)' }}
          />
        </div>
        <header className="px-5 pb-2 pt-3">
          <h2 className="text-[17px] font-bold text-ink">{title}</h2>
          {subtitle ? <p className="mt-0.5 text-[13px] text-muted">{subtitle}</p> : null}
        </header>
        <div className="thin-scroll flex-1 overflow-y-auto px-5 pb-4">{children}</div>
        {footer ? <footer className="flex gap-2 border-t border-line px-5 py-3">{footer}</footer> : null}
      </div>
    </div>
  );
}

/**
 * A form outcome, per the house rule: the result of a submit is a modal over
 * the form, not a line of text that scrolls away unnoticed.
 */
export function OutcomeModal({
  open,
  onClose,
  ok,
  title,
  message,
  children,
}: {
  open: boolean;
  onClose: () => void;
  ok: boolean;
  title: string;
  message: string;
  children?: ReactNode;
}) {
  const close = useCallback(() => onClose(), [onClose]);
  return (
    <Modal
      open={open}
      onClose={close}
      title={title}
      footer={
        <button type="button" className="btn btn-primary" onClick={close}>
          {ok ? 'Done' : 'Back to the form'}
        </button>
      }
    >
      <div
        className="rounded-card border p-4 text-[14px] leading-relaxed"
        style={{
          background: ok ? 'var(--sp-accent-soft)' : 'var(--sp-warn-soft)',
          borderColor: ok ? 'var(--sp-accent-border)' : 'var(--sp-warn-border)',
          color: 'var(--sp-text)',
        }}
      >
        {message}
      </div>
      {children ? <div className="mt-4">{children}</div> : null}
    </Modal>
  );
}
